import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { useBudgetPDF } from '../../../../../Hooks/useBudgetPDF';
import { useQuoteAdjustmentDraft } from '../../../../../Hooks/useQuoteAdjustmentDraft';
import axios from 'axios';
import { url, api } from '../../../../../env';
import { AppTextField, AppTextareaField } from '../../../../../Components/FormKit';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
    }).format(value || 0);
};

const cardHeader = (title) => (
    <div style={{
        background: '#f8fafc',
        padding: '0.65rem 1rem',
        borderBottom: '1px solid #e2e8f0',
        borderRadius: '14px 14px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    }}>
        <span style={{ fontWeight: 700, fontSize: '0.72rem', color: '#1565C0', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
            {title}
        </span>
        <div style={{ flex: 1, height: '1px', background: '#bfdbfe' }} />
    </div>
);


const defaultEmailContent = ({ clientName, formattedDownPayment, uploadPaymentUrl }) => {
    return `¡Felicidades, ${clientName}! 🎉

Nos emociona darte la bienvenida al equipo Peicar, el equipo que deduce impuestos mientras manejas.

Te adjuntamos la cotización autorizada y eso significa que ¡el camino hacia tu nuevo auto ya comenzó!
Solo queda un paso: realizar el pago inicial de ${formattedDownPayment} a la cuenta de Peicar. Con eso, ¡podríamos iniciar la firma del contrato!

Datos bancarios:

Banco: BBVA
No. de Cuenta: 0121006193
CLABE: 012180001210061937
RFC: CCA2308224G3

Pero eso no es todo…

🚘 Tu arrendamiento incluye accesorios adicionales, ¡así que no te preocupes!
Nosotros nos encargamos de TODO:
✅ Seguro por todo el plazo
✅ Placas y tenencias
✅ Impuestos incluidos
✅ Y tú solo tienes que llevarlo a servicio a la Toyota que más te guste

Así de simple. Nosotros hacemos el trabajo pesado, tú solo disfrutas tu auto y deduces al máximo.

🔔 OJO: Cuando realices tu pago inicial, avísanos de inmediato.

De esta forma podremos enviarte tu factura de inmediato.

Gracias por confiar en Peicar para ser tu aliado de movilidad fiscal e inteligente.

¡Vamos con todo!

Saludos,
Equipo Peicar`
};

export const EmailConfirmationModal = ({ visible, onHide, initialEmail = '', persona, toast, stepStatus, stepData, onSuccess }) => {
    const isCompleted = stepStatus === 'completed';
    // When there's an active draft, the modal should be editable even if step was previously completed
    const [isEditableOverride, setIsEditableOverride] = useState(false);
    const isEditable = !isCompleted || isEditableOverride;
    const [email, setEmail] = useState(initialEmail);

    // Generar asunto dinámico con el modelo del vehículo
    const vehicleModel = persona?.infoBudget?.vehicle?.model || 'vehículo';
    const vehicleBrand = persona?.infoBudget?.vehicle?.brand || '';
    const vehicleFullName = vehicleBrand ? `${vehicleBrand} ${vehicleModel}` : vehicleModel;

    const [subject, setSubject] = useState(`🚗 Cotización Autorizada - ¡Bienvenido a Peicar, tu ${vehicleFullName} es casi tuyo!`);
    const [ccEmails, setCcEmails] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const fileInputRef = useRef(null);

    // Use PDF hook for automatic PDF attachment
    const { getBudgetPDFData, loading: loadingPDF } = useBudgetPDF(persona, toast);
    const [budgetPDF, setBudgetPDF] = useState(null);
    const [pdfError, setPdfError] = useState(false);
    const [attachPDF, setAttachPDF] = useState(true); // Por defecto adjuntar el PDF
    const [previewAttachment, setPreviewAttachment] = useState(null); // { file_name, blob_url }

    // Draft detection for recotización
    const { getDraftsByBudget, approveDraft } = useQuoteAdjustmentDraft();
    const [activeDraft, setActiveDraft] = useState(null);
    const [loadingDraft, setLoadingDraft] = useState(false);


    // Obtener el monto del pago inicial y formatearlo
    const downPayment = persona?.infoBudget?.financial_details?.groupConceptsEnganche?.totalWitIva
    const formattedDownPayment = new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2
    }).format(downPayment || 0);

    // URL de prueba para subir comprobante (cambiar por la real después)
    const uploadPaymentUrl = `https://peicar.com/upload-payment/${persona?.id || 'TEMP_ID'}`;

    const clientName = persona?.name || 'Quant Capital';

    const [emailContent, setEmailContent] = useState(defaultEmailContent({
        clientName,
        formattedDownPayment,
        uploadPaymentUrl
    }));


    // Fetch active drafts when modal opens — always check, even if step is completed
    useEffect(() => {
        if (visible && persona?.infoBudget?.budget_id) {
            setLoadingDraft(true);
            getDraftsByBudget(persona.infoBudget.budget_id)
                .then(result => {
                    if (result?.success && result.data) {
                        const readyDraft = result.data.find(d => d.status === 'ready_for_review');
                        setActiveDraft(readyDraft || null);
                        // If completed but draft exists, enable editing
                        if (readyDraft && isCompleted) {
                            setIsEditableOverride(true);
                        }
                    } else {
                        setActiveDraft(null);
                        setIsEditableOverride(false);
                    }
                })
                .catch(() => {
                    setActiveDraft(null);
                    setIsEditableOverride(false);
                })
                .finally(() => setLoadingDraft(false));
        } else {
            setActiveDraft(null);
            setIsEditableOverride(false);
        }
    }, [visible, persona?.infoBudget?.budget_id, isCompleted]);

    // Fetch PDF when modal opens
    useEffect(() => {
        if (visible && persona?.infoBudget?.budget_id) {
            // Si está completado Y no hay draft activo, cargar datos guardados en read-only
            if (isCompleted && !activeDraft && stepData?.metadata) {
                setEmail(stepData.metadata.email_sent_to || initialEmail);
                setSubject(stepData.metadata.subject || '');
                setEmailContent(stepData.metadata.content || '');
                setAttachPDF(false);
                return;
            }

            // If completed WITH active draft: load saved email data but also fetch the adjusted PDF
            if (isCompleted && activeDraft && stepData?.metadata) {
                setEmail(stepData.metadata.email_sent_to || initialEmail);
                setSubject(stepData.metadata.subject || '');
                setEmailContent(stepData.metadata.content || '');
            }

            // Reset PDF states
            setBudgetPDF(null);
            setPdfError(false);
            setAttachPDF(true);

            // Fetch PDF — always from server (budget already mutated by cotizador2)
            if (stepStatus === 'requote' || !activeDraft?.quote_preview) {
                getBudgetPDFData().then(pdfFile => {
                    if (pdfFile) {
                        setBudgetPDF(pdfFile);
                    } else {
                        setPdfError(true);
                    }
                });
            } else if (activeDraft?.quote_preview) {
                getBudgetPDFData(activeDraft.quote_preview).then(pdfFile => {
                    if (pdfFile) {
                        setBudgetPDF(pdfFile);
                    } else {
                        setPdfError(true);
                    }
                });
            }
        }
    }, [visible, persona?.infoBudget?.budget_id, isCompleted, stepData, activeDraft]);

    // Override email content when step is in requote status (needs client re-approval)
    useEffect(() => {
        if (visible && stepStatus === 'requote') {
            // Build change details from active draft
            const changes = [];
            const draft = activeDraft;

            if (draft?.diff) {
                if (draft.diff.price_before && draft.diff.price_after && draft.diff.price_before !== draft.diff.price_after) {
                    changes.push(`• Precio: ${formatCurrency(draft.diff.price_before)} → ${formatCurrency(draft.diff.price_after)}`);
                }
                if (draft.diff.vehicle_changed) {
                    changes.push(`• Se actualizó el vehículo`);
                }
            }

            if (draft?.payment_impact && draft.payment_impact.action_needed !== 'none') {
                const pi = draft.payment_impact;
                changes.push(`• Pago inicial: ${formatCurrency(pi.previous_payment)} → ${formatCurrency(pi.new_payment)}`);
            }

            const changesBlock = changes.length > 0
                ? `\n📋 Cambios realizados:\n${changes.join('\n')}\n`
                : '';

            let paymentNote = '';
            if (draft?.payment_impact) {
                const pi = draft.payment_impact;
                if (pi.action_needed === 'register_surplus') {
                    paymentNote = `\nYa cuentas con un saldo a favor de ${formatCurrency(pi.surplus)}.\n`;
                } else if (pi.action_needed === 'request_additional_payment') {
                    paymentNote = `\nSe requiere un pago adicional de ${formatCurrency(pi.shortfall)}.\n`;
                } else if (pi.action_needed === 'none' && pi.total_already_paid > 0) {
                    paymentNote = `\nTus pagos anteriores cubren el monto requerido. No se requiere pago adicional.\n`;
                }
            }

            setSubject(`📋 Actualización de Cotización - ${vehicleFullName}`);
            setEmailContent(`Hola ${clientName},

Se realizó una modificación en tu cotización para tu ${vehicleFullName}.
${changesBlock}
El nuevo pago inicial es de ${formattedDownPayment}.${paymentNote}
Adjuntamos la cotización actualizada para tu revisión. Por favor confirma tu aprobación para continuar con el proceso.

Datos bancarios:

Banco: BBVA
No. de Cuenta: 0121006193
CLABE: 012180001210061937
RFC: CCA2308224G3

Si tienes alguna duda, no dudes en contactarnos.

Saludos,
Equipo Peicar`);
        }
    }, [visible, stepStatus, activeDraft]);


    // Validación de campos
    const validateFields = () => {
        const errors = [];

        // Validar email principal
        if (!email || email.trim() === '') {
            errors.push('El correo del destinatario es requerido');
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                errors.push('El correo del destinatario no es válido');
            }
        }

        // Validar asunto
        if (!subject || subject.trim() === '') {
            errors.push('El asunto es requerido');
        }

        // Validar contenido
        if (!emailContent || emailContent.trim() === '') {
            errors.push('El contenido del correo es requerido');
        }

        // Validar correos CC
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        ccEmails.forEach((cc, index) => {
            if (cc && cc.trim() !== '' && !emailRegex.test(cc)) {
                errors.push(`El correo CC #${index + 1} no es válido`);
            }
        });

        // Validar persona_id
        if (!persona?.id) {
            errors.push('No se pudo identificar el cliente');
        }

        return errors;
    };

    const handleSendEmail = async () => {
        // Validar campos
        const errors = validateFields();
        if (errors.length > 0) {
            errors.forEach(error => {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error de validación',
                    detail: error,
                    life: 4000
                });
            });
            return;
        }

        setSending(true);

        try {
            // Preparar FormData para enviar archivos
            const formData = new FormData();
            formData.append('email', email);
            formData.append('subject', subject);
            formData.append('content', emailContent);
            formData.append('persona_id', persona.id);
            formData.append('budget_id', persona.infoBudget.budget_id);
            formData.append('typePersona', persona.tipo_persona);

            // Include draft ID if there's an active recotización draft
            if (activeDraft?.id) {
                formData.append('quote_adjustment_draft_id', activeDraft.id);
            }

            // Agregar CC emails (solo los que no estén vacíos)
            const validCc = ccEmails.filter(cc => cc && cc.trim() !== '');
            validCc.forEach((cc, index) => {
                formData.append(`cc[${index}]`, cc);
            });

            // Agregar el PDF de la cotización automáticamente (si está disponible y marcado para adjuntar)
            let attachmentIndex = 0;
            if (budgetPDF && attachPDF) {
                formData.append(`attachments[${attachmentIndex}]`, budgetPDF);
                attachmentIndex++;
            }

            // Agregar archivos adjuntos adicionales
            uploadedFiles.forEach((file) => {
                formData.append(`attachments[${attachmentIndex}]`, file);
                attachmentIndex++;
            });

            // Obtener token de localStorage
            const token = localStorage.getItem('token');

            // Enviar petición al backend
            const response = await axios.post(`${url}/send-confirmation-email`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                }
            });

            if (response.data.success) {
                // If requote, approve the draft (confirm_quote → completed, draft → approved)
                if (stepStatus === 'requote' && activeDraft?.id && activeDraft.status !== 'approved') {
                    try {
                        await approveDraft(activeDraft.id);
                    } catch (err) {
                        console.warn('Error al aprobar draft después de envío:', err);
                    }
                }

                toast.current?.show({
                    severity: 'success',
                    summary: 'Correo enviado',
                    detail: stepStatus === 'requote'
                        ? 'Correo enviado y recotización aprobada'
                        : 'El correo de confirmación se ha enviado exitosamente',
                    life: 3000
                });

                // Actualizar estado en el padre si se proporciona callback
                if (onSuccess && response.data.data) {
                    onSuccess(response.data.data);
                }

                // Cerrar el modal después de enviar
                onHide();
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.data.message || 'Error al enviar el correo',
                    life: 4000
                });
            }

        } catch (error) {
            console.error('Error al enviar correo:', error);

            let errorMessage = 'Error al enviar el correo';

            if (error.response?.data?.errors) {
                // Mostrar errores de validación del backend
                Object.values(error.response.data.errors).forEach(errorArray => {
                    errorArray.forEach(err => {
                        toast.current?.show({
                            severity: 'error',
                            summary: 'Error',
                            detail: err,
                            life: 4000
                        });
                    });
                });
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: errorMessage,
                    life: 4000
                });
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: errorMessage,
                    life: 4000
                });
            }
        } finally {
            setSending(false);
        }
    };

    const handleFileSelect = (e) => {
        const added = Array.from(e.target.files || []);
        const updated = [...uploadedFiles, ...added];
        setUploadedFiles(updated);
        e.target.value = '';
    };

    const removeFile = (index) => {
        setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
    };

    // Funciones para CC
    const addCcEmail = () => {
        setCcEmails([...ccEmails, '']);
    };

    const updateCcEmail = (index, value) => {
        const newCcEmails = [...ccEmails];
        newCcEmails[index] = value;
        setCcEmails(newCcEmails);
    };

    const removeCcEmail = (index) => {
        setCcEmails(ccEmails.filter((_, i) => i !== index));
    };

    const hasSavedFiles = isCompleted && stepData?.files && stepData.files.length > 0;

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            style={{ width: '50vw', maxWidth: '900px', minWidth: '600px', opacity: "inherit", boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
            breakpoints={{ '960px': '90vw', '641px': '95vw' }}
            contentStyle={{ maxHeight: '75vh', overflowY: 'auto', overscrollBehavior: 'contain' }}
            header={
                <div className="flex align-items-center gap-2">
                    <h2
                        style={{
                            background: "linear-gradient(90deg, #21588C 6.22%, #21588C 77.34%)",
                            WebkitBackgroundClip: "text",
                            backgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            margin: 0
                        }}
                    >
                        {isCompleted && !activeDraft ? 'Detalle del Correo Enviado'
                            : activeDraft ? 'Reenviar Cotización Ajustada'
                                : stepStatus === 'requote' ? 'Re-aprobación de Cotización'
                                    : 'Correo de Confirmación'}
                    </h2>
                </div>
            }
            modal
            draggable={false}
        >
            <style>
                {`
                    /* Modal redondeado para coincidir con las tk-table-card */
                    .p-dialog {
                        background: #ffffff !important;
                        overflow: hidden !important;
                        border-radius: 16px !important;
                    }
                    .p-dialog-header {
                        background: #ffffff !important;
                        margin-bottom: 0 !important;
                        border-top-left-radius: 16px !important;
                        border-top-right-radius: 16px !important;
                    }
                    .p-dialog-content {
                        background: #ffffff !important;
                        margin-top: 0 !important;
                        border-bottom-left-radius: 16px !important;
                        border-bottom-right-radius: 16px !important;
                    }
                `}
            </style>

            <div className="email-modal-content fk-root flex flex-column gap-3">
                {/* Recotización Pendiente Banner */}
                {isEditable && activeDraft && (
                    <div style={{
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fff7ed 100%)',
                        border: '1px solid #f59e0b',
                        borderRadius: '12px',
                        padding: '1rem 1.25rem',
                    }}>
                        <div className="flex align-items-center gap-2 mb-2">
                            <i className="pi pi-exclamation-triangle" style={{ color: '#d97706', fontSize: '1.25rem' }}></i>
                            <span className="font-bold" style={{ color: '#92400e' }}>Recotización pendiente</span>
                            <Tag value="Ajuste por factura" severity="warning" style={{ fontSize: '0.7rem' }} />
                        </div>
                        <p className="text-sm m-0 mb-2" style={{ color: '#78350f' }}>
                            Al enviar este correo, se aplicarán automáticamente los ajustes de la recotización al presupuesto del cliente.
                        </p>

                        {/* Price diff */}
                        {activeDraft.diff?.price_before !== undefined && activeDraft.diff?.price_before !== null && activeDraft.diff?.price_after !== null && (
                            <div className="flex align-items-center gap-2 mb-2 p-2 border-round" style={{ background: 'rgba(255,255,255,0.7)' }}>
                                <i className="pi pi-dollar" style={{ color: '#d97706' }}></i>
                                <span className="text-sm">Precio:</span>
                                <span className="text-sm line-through" style={{ color: '#9ca3af' }}>
                                    {formatCurrency(activeDraft.diff.price_before)}
                                </span>
                                <i className="pi pi-arrow-right text-xs" style={{ color: '#9ca3af' }}></i>
                                <span className="text-sm font-bold" style={{ color: '#059669' }}>
                                    {formatCurrency(activeDraft.diff.price_after)}
                                </span>
                            </div>
                        )}

                        {/* Vehicle change */}
                        {activeDraft.diff?.vehicle_changed && (
                            <div className="flex align-items-center gap-2 mb-2 p-2 border-round" style={{ background: 'rgba(255,255,255,0.7)' }}>
                                <i className="pi pi-car" style={{ color: '#dc2626' }}></i>
                                <span className="text-sm font-medium" style={{ color: '#991b1b' }}>Cambio de vehículo detectado</span>
                            </div>
                        )}

                        {/* Change summary */}
                        {activeDraft.diff?.change_summary && activeDraft.diff.change_summary.length > 0 && (
                            <div className="p-2 border-round" style={{ background: 'rgba(255,255,255,0.7)' }}>
                                <small className="text-gray-600 block mb-1">Resumen de cambios:</small>
                                {activeDraft.diff.change_summary.map((summary, idx) => (
                                    <div key={idx} className="text-xs text-gray-700 mb-1">
                                        <i className="pi pi-chevron-right mr-1" style={{ fontSize: '0.6rem' }}></i>
                                        {summary}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Payment impact */}
                        {activeDraft.payment_impact && activeDraft.payment_impact.action_needed !== 'none' && (
                            <div className="mt-2 p-2 border-round" style={{
                                background: activeDraft.payment_impact.action_needed === 'request_additional_payment'
                                    ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                                border: `1px solid ${activeDraft.payment_impact.action_needed === 'request_additional_payment' ? '#fecaca' : '#a7f3d0'}`
                            }}>
                                <div className="flex align-items-center gap-2">
                                    <i className={`pi ${activeDraft.payment_impact.action_needed === 'request_additional_payment'
                                        ? 'pi-exclamation-circle text-red-500' : 'pi-info-circle text-green-500'}`}></i>
                                    <span className="text-sm font-medium">
                                        {activeDraft.payment_impact.action_needed === 'request_additional_payment'
                                            ? `Faltante de pago: ${formatCurrency(activeDraft.payment_impact.shortfall)}`
                                            : `Saldo a favor: ${formatCurrency(activeDraft.payment_impact.surplus)}`
                                        }
                                    </span>
                                </div>
                            </div>
                        )}

                    </div>
                )}

                {loadingDraft && (
                    <div className="flex align-items-center gap-2 p-2 bg-blue-50 border-round">
                        <i className="pi pi-spin pi-spinner text-blue-500"></i>
                        <span className="text-sm text-blue-700">Verificando recotizaciones pendientes...</span>
                    </div>
                )}

                {/* Requote banner - shown when step is in requote status (draft already approved) */}
                {stepStatus === 'requote' && !activeDraft && (
                    <div style={{
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fff7ed 100%)',
                        border: '1px solid #f59e0b',
                        borderRadius: '12px',
                        padding: '1rem 1.25rem',
                    }}>
                        <div className="flex align-items-center gap-2 mb-2">
                            <i className="pi pi-info-circle" style={{ color: '#d97706', fontSize: '1.25rem' }}></i>
                            <span className="font-bold" style={{ color: '#92400e' }}>Re-aprobación requerida</span>
                        </div>
                        <p className="text-sm m-0" style={{ color: '#78350f' }}>
                            La cotización fue modificada, se requiere re-aprobación del cliente. Envía el correo con los nuevos detalles para su revisión.
                        </p>
                    </div>
                )}

                {/* Card 1: Destinatario y Copias */}
                <div className="tk-table-card">
                    {cardHeader('Destinatario y Copias')}
                    <div style={{ padding: '1rem' }} className="flex flex-column gap-3">
                        <AppTextField
                            label="Correo del destinatario"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="correo@ejemplo.com"
                            disabled={!isEditable}
                        />

                        {/* CC */}
                        <div className="flex flex-column gap-2">
                            <div className="flex align-items-center justify-content-between">
                                <span className="fk-label" style={{ margin: 0 }}>CC — Con Copia</span>
                                {isEditable && (
                                    <button
                                        type="button"
                                        onClick={addCcEmail}
                                        className="fk-btn fk-btn--secondary fk-btn--sm"
                                        style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                                    >
                                        <i className="pi pi-plus" style={{ fontSize: '0.7rem' }} />
                                        Agregar CC
                                    </button>
                                )}
                            </div>
                            {ccEmails.map((ccEmail, index) => (
                                <div key={index} className="flex gap-2 align-items-center">
                                    <div className="fk-input flex-1">
                                        <input
                                            className="p-inputtext"
                                            value={ccEmail}
                                            onChange={(e) => updateCcEmail(index, e.target.value)}
                                            placeholder="destinatario@ejemplo.com"
                                            disabled={!isEditable}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    {isEditable && (
                                        <button
                                            type="button"
                                            className="fk-icon-btn fk-icon-btn--danger"
                                            onClick={() => removeCcEmail(index)}
                                            title="Eliminar"
                                        >
                                            <i className="pi pi-times" style={{ fontSize: '0.8rem' }} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            {ccEmails.length === 0 && (
                                <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                    Sin destinatarios en copia
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Card 2: Configuración del Correo */}
                <div className="tk-table-card">
                    {cardHeader('Configuración del Correo')}
                    <div style={{ padding: '1rem' }} className="flex flex-column gap-3">
                        <AppTextField
                            label="Asunto"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Asunto del correo"
                            disabled={!isEditable}
                        />
                        <AppTextareaField
                            label="Contenido del correo"
                            value={emailContent}
                            onChange={(e) => setEmailContent(e.target.value)}
                            rows={12}
                            placeholder="Escribe el mensaje aquí..."
                            readOnly={!isEditable}
                        />
                    </div>
                </div>

                {/* Card 3: Documentos y Adjuntos */}
                {(isEditable || hasSavedFiles) && (
                    <div className="tk-table-card">
                        {cardHeader('Documentos y Adjuntos')}
                        <div style={{ padding: '1rem' }} className="flex flex-column gap-3">

                            {/* PDF Automático de la Cotización — chip estilo Visita Ocular */}
                            {isEditable && (
                                <div className="flex align-items-center gap-2 flex-wrap">
                                    {loadingPDF && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '99px', padding: '0.25rem 0.7rem', fontSize: '0.72rem', color: '#1e40af', fontWeight: 600 }}>
                                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.7rem' }} />
                                            Cargando cotización…
                                        </span>
                                    )}

                                    {!loadingPDF && pdfError && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '99px', padding: '0.25rem 0.7rem', fontSize: '0.72rem', color: '#b91c1c', fontWeight: 600 }}>
                                            <i className="pi pi-exclamation-triangle" style={{ fontSize: '0.7rem' }} />
                                            Error al cargar PDF de cotización
                                        </span>
                                    )}

                                    {!loadingPDF && budgetPDF && attachPDF && (
                                        <span
                                            title="Cotización adjunta automáticamente"
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.35rem',
                                                background: '#eff6ff',
                                                border: '1px solid #bfdbfe',
                                                borderRadius: '99px',
                                                padding: '0.2rem 0.4rem 0.2rem 0.55rem',
                                                fontSize: '0.72rem',
                                                color: '#1e40af',
                                                fontWeight: 600,
                                                maxWidth: '320px',
                                                boxShadow: '0 1px 2px rgba(30, 64, 175, 0.06)',
                                            }}
                                        >
                                            <i className="pi pi-shield" style={{ fontSize: '0.7rem', color: '#2563eb', flexShrink: 0 }} />
                                            <span style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                background: '#dbeafe',
                                                border: '1px solid #bfdbfe',
                                                color: '#1d4ed8',
                                                padding: '0.05rem 0.4rem',
                                                borderRadius: '99px',
                                                fontSize: '0.6rem',
                                                fontWeight: 700,
                                                letterSpacing: '0.04em',
                                                textTransform: 'uppercase',
                                                flexShrink: 0,
                                            }}>
                                                Cotización
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setPreviewAttachment({ file_name: budgetPDF.name, blob: budgetPDF })}
                                                title={`Vista previa: ${budgetPDF.name}`}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    color: '#1e40af',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: '160px',
                                                    cursor: 'pointer',
                                                    fontWeight: 600,
                                                    fontSize: '0.72rem',
                                                }}
                                            >
                                                {budgetPDF.name}
                                            </button>
                                            <span style={{ color: '#94a3b8', flexShrink: 0, fontSize: '0.66rem', fontWeight: 500 }}>
                                                ({(budgetPDF.size / 1024).toFixed(0)} KB)
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setAttachPDF(false)}
                                                title="Quitar este adjunto del correo"
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', padding: '0 0.1rem', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                                            >
                                                <i className="pi pi-times" style={{ fontSize: '0.62rem' }} />
                                            </button>
                                        </span>
                                    )}

                                    {!loadingPDF && budgetPDF && !attachPDF && (
                                        <span
                                            title="Removido — clic para volver a adjuntar"
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.35rem',
                                                background: '#f1f5f9',
                                                border: '1px dashed #cbd5e1',
                                                borderRadius: '99px',
                                                padding: '0.2rem 0.4rem 0.2rem 0.55rem',
                                                fontSize: '0.7rem',
                                                color: '#64748b',
                                                fontWeight: 500,
                                                maxWidth: '320px',
                                                textDecoration: 'line-through',
                                            }}
                                        >
                                            <i className="pi pi-ban" style={{ fontSize: '0.7rem', color: '#94a3b8', flexShrink: 0 }} />
                                            <span style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#94a3b8', flexShrink: 0 }}>
                                                Cotización
                                            </span>
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>
                                                {budgetPDF.name}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setAttachPDF(true)}
                                                title="Restaurar este adjunto"
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', padding: '0 0.1rem', display: 'flex', alignItems: 'center', flexShrink: 0, textDecoration: 'none' }}
                                            >
                                                <i className="pi pi-undo" style={{ fontSize: '0.62rem' }} />
                                            </button>
                                        </span>
                                    )}

                                    {!loadingPDF && !budgetPDF && !pdfError && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.7rem', color: '#475569' }}>
                                            <i className="pi pi-info-circle" style={{ fontSize: '0.7rem', color: '#2563eb' }} />
                                            Se incluirá la cotización automáticamente.
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Adjuntar archivos nuevos */}
                            {isEditable && (
                                <div className="flex flex-column gap-2">
                                    <div className="flex align-items-center gap-2 flex-wrap">
                                        <button
                                            type="button"
                                            className="fk-btn fk-btn--secondary fk-btn--sm"
                                            onClick={() => fileInputRef.current?.click()}
                                            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}
                                        >
                                            <i className="pi pi-paperclip" style={{ fontSize: '0.72rem' }} />
                                            Adjuntar documentos
                                        </button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            accept="application/pdf,image/*"
                                            style={{ display: 'none' }}
                                            onChange={handleFileSelect}
                                        />
                                        {uploadedFiles.length === 0 && (
                                            <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>Sin archivos adjuntos</span>
                                        )}
                                        {uploadedFiles.map((file, i) => (
                                            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '99px', padding: '0.2rem 0.4rem 0.2rem 0.65rem', fontSize: '0.73rem', color: '#334155', maxWidth: '220px' }}>
                                                <i className="pi pi-file" style={{ fontSize: '0.68rem', flexShrink: 0 }} />
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                                                <span style={{ color: '#94a3b8', flexShrink: 0 }}>({(file.size / 1024).toFixed(0)} KB)</span>
                                                <button type="button" onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0 0.1rem', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                                    <i className="pi pi-times" style={{ fontSize: '0.62rem' }} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                    <small className="text-gray-500">
                                        Formatos permitidos: PDF e imágenes. Máximo 12MB por archivo.
                                    </small>
                                </div>
                            )}

                            {/* Historial: archivos enviados (cuando ya completado) */}
                            {hasSavedFiles && (
                                <>
                                    {isEditable && <div style={{ height: '1px', background: '#e2e8f0' }} />}
                                    <div>
                                        <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.72rem', fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.25rem' }}>
                                            Archivos Enviados
                                        </h4>
                                        <div className="flex flex-wrap gap-2">
                                            {stepData.files.map((file, index) => (
                                                <div key={index} className="flex align-items-center gap-2 p-2 border-1 border-gray-300 border-round bg-white shadow-1" style={{ minWidth: '200px' }}>
                                                    <i className={`pi ${file.file_name?.endsWith('.pdf') ? 'pi-file-pdf text-red-500' : 'pi-image text-blue-500'} text-xl`}></i>
                                                    <div className="flex flex-column" style={{ maxWidth: '160px' }}>
                                                        <span className="text-sm font-medium text-gray-800 white-space-nowrap overflow-hidden text-overflow-ellipsis" title={file.file_name}>{file.file_name}</span>
                                                        <span className="text-xs text-gray-500">{new Date(file.created_at).toLocaleDateString()}</span>
                                                    </div>
                                                    {file.s3_url && (
                                                        <a href={file.s3_url} target="_blank" rel="noopener noreferrer" className="ml-auto flex align-items-center p-1 hover:bg-gray-100 border-round">
                                                            <i className="pi pi-eye text-blue-600 cursor-pointer"></i>
                                                        </a>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Botones de acción */}
                <div className="flex justify-content-end gap-2 mt-2 pt-3 border-top-1 border-gray-200">
                    <Button
                        label="Cancelar"
                        icon="pi pi-times"
                        className="p-button-outlined p-button-secondary"
                        onClick={onHide}
                        disabled={sending}
                    />
                    {isEditable && (
                        <Button
                            label={sending
                                ? (activeDraft ? "Aplicando ajustes y enviando..." : "Enviando...")
                                : (activeDraft ? "Enviar cotización ajustada" : "Enviar Correo")
                            }
                            icon={sending ? "pi pi-spin pi-spinner" : (activeDraft ? "pi pi-check-circle" : "pi pi-send")}
                            className="Button Green"
                            onClick={handleSendEmail}
                            disabled={sending || loadingDraft}
                            loading={sending}
                            style={activeDraft ? {
                                background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
                                border: 'none',
                            } : undefined}
                        />
                    )}
                </div>
            </div>

            {/* Modal de Vista Previa del PDF de cotización */}
            <Dialog
                visible={!!previewAttachment}
                onHide={() => setPreviewAttachment(null)}
                modal
                dismissableMask
                header={previewAttachment?.file_name || 'Vista previa'}
                style={{ width: '90vw', maxWidth: '1100px', height: '88vh' }}
                contentStyle={{ padding: 0, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            >
                {previewAttachment?.blob ? (
                    <PreviewIframe blob={previewAttachment.blob} title={previewAttachment.file_name} />
                ) : (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                        No hay vista previa disponible.
                    </div>
                )}
            </Dialog>
        </Dialog >
    );
};

// Genera un objectURL del blob solo mientras el preview esté visible y lo libera al desmontar.
const PreviewIframe = ({ blob, title }) => {
    const [src, setSrc] = useState(null);
    useEffect(() => {
        if (!blob) return undefined;
        const objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [blob]);
    if (!src) return null;
    return (
        <iframe
            title={title}
            src={src}
            style={{ flex: 1, width: '100%', border: 'none', background: '#f8fafc' }}
        />
    );
};
